import { supabase } from '@/shared/lib/supabase';
import type { FutebolBolaoPalpite, FutebolCraqueBagreVoto, FutebolJogoDia, FutebolPartidaAtiva, FutebolPartidaHistorico, FutebolPartidaHistoricoItem, FutebolTermometroContagem } from '@/shared/types';
import type { IFutebolRepository } from '../domain/ports/IFutebolRepository';
import { calcularRangeDiaBRT } from '@/shared/lib/dateUtils';

export const futebolRepository: IFutebolRepository = {
  async listarJogosDoDia(): Promise<FutebolJogoDia[]> {
    const { inicio, fim } = calcularRangeDiaBRT();
    const { data, error } = await supabase
      .from('futebol_jogos_dia')
      .select('*')
      .gte('horario', inicio)
      .lt('horario', fim)
      .order('horario', { ascending: true });
    if (error) throw error;
    return (data ?? []) as FutebolJogoDia[];
  },

  async buscarPartidaAtiva(telaId: string): Promise<FutebolPartidaAtiva | null> {
    const { data, error } = await supabase
      .from('futebol_partida_ativa')
      .select('*')
      .eq('tela_id', telaId)
      .maybeSingle();
    if (error) throw error;
    return (data as FutebolPartidaAtiva | null) ?? null;
  },

  async buscarPartidaAtivaAdmin(empresaId?: string): Promise<FutebolPartidaAtiva | null> {
    const { data, error } = await supabase.rpc('buscar_partida_ativa_admin' as never, {
      ...(empresaId ? { p_empresa_id: empresaId } : {}),
    } as never);
    if (error) throw error;
    const rows = (data ?? []) as FutebolPartidaAtiva[];
    return rows[0] ?? null;
  },

  async buscarJogoPorFixtureId(fixtureId: number): Promise<FutebolJogoDia | null> {
    const { data, error } = await supabase
      .from('futebol_jogos_dia')
      .select('*')
      .eq('api_fixture_id', fixtureId)
      .maybeSingle();
    if (error) throw error;
    return (data as FutebolJogoDia | null) ?? null;
  },

  async ativarJogo(fixtureId: number, empresaId?: string): Promise<void> {
    const { error } = await supabase.rpc('ativar_jogo_admin' as never, {
      p_fixture_id: fixtureId,
      ...(empresaId ? { p_empresa_id: empresaId } : {}),
    } as never);
    if (error) throw error;
  },

  async desativarJogo(empresaId?: string): Promise<void> {
    const { error } = await supabase.rpc('desativar_jogo_admin' as never, {
      ...(empresaId ? { p_empresa_id: empresaId } : {}),
    } as never);
    if (error) throw error;
  },

  async inserirPalpiteBolao(palpite) {
    // INSERT via RPC SECURITY DEFINER validada (anon não tem INSERT direto).
    // Valida tela, fixture, faixa 0–99 e identificação server-side.
    const { data, error } = await supabase.rpc('registrar_palpite_bolao' as never, {
      p_tela_id:           palpite.tela_id,
      p_fixture_id:        palpite.fixture_id,
      p_session_id:        palpite.session_id,
      p_identificacao:     palpite.identificacao,
      p_palpite_casa:      palpite.palpite_casa,
      p_palpite_visitante: palpite.palpite_visitante,
    } as never);
    if (error) throw error;
    if (data === 'ok') return;
    // 'mesa_ocupada' mantém o comportamento legado de unique_violation (23505)
    // que a UI já trata exibindo "mesa já apostou".
    if (data === 'mesa_ocupada') throw Object.assign(new Error('mesa_ocupada'), { code: '23505' });
    throw new Error(typeof data === 'string' ? data : 'erro_palpite');
  },

  async mesaJaApostou(telaId, fixtureId, identificacao) {
    const { data, error } = await supabase
      .from('futebol_bolao_palpites' as never)
      .select('id')
      .eq('tela_id', telaId)
      .eq('fixture_id', fixtureId)
      .eq('identificacao', identificacao)
      .maybeSingle();
    if (error) throw error;
    return data !== null;
  },

  async buscarVencedoresBolao(telaId, fixtureId, placarCasa, placarVisitante) {
    const { data, error } = await supabase
      .from('futebol_bolao_palpites' as never)
      .select('*')
      .eq('tela_id', telaId)
      .eq('fixture_id', fixtureId)
      .eq('palpite_casa', placarCasa)
      .eq('palpite_visitante', placarVisitante);
    if (error) throw error;
    return (data ?? []) as FutebolBolaoPalpite[];
  },

  async buscarTodosPalpites(telaId, fixtureId) {
    const { data, error } = await supabase
      .from('futebol_bolao_palpites' as never)
      .select('*')
      .eq('tela_id', telaId)
      .eq('fixture_id', fixtureId)
      .order('identificacao', { ascending: true });
    if (error) throw error;
    return (data ?? []) as FutebolBolaoPalpite[];
  },

  async buscarPalpitesAdmin(fixtureId, empresaId) {
    const { data, error } = await supabase
      .from('futebol_bolao_palpites' as never)
      .select('*, telas!inner(empresa_id)' as never)
      .eq('fixture_id' as never, fixtureId)
      .eq('telas.empresa_id' as never, empresaId)
      .order('identificacao', { ascending: true });
    if (error) throw error;
    return (data ?? []) as FutebolBolaoPalpite[];
  },

  async listarHistoricoDia(telaId: string): Promise<FutebolPartidaHistoricoItem[]> {
    const { inicio, fim } = calcularRangeDiaBRT();
    const { data, error } = await supabase
      .from('futebol_partida_historico' as never)
      .select('*')
      .eq('tela_id', telaId)
      .gte('encerrado_em', inicio)
      .lt('encerrado_em', fim)
      .order('encerrado_em', { ascending: false });
    if (error) throw error;

    const historico = (data ?? []) as FutebolPartidaHistorico[];
    if (historico.length === 0) return [];

    const fixtureIds = [...new Set(historico.map((h) => h.fixture_id))];
    const { data: jogos, error: jogosErr } = await supabase
      .from('futebol_jogos_dia')
      .select('api_fixture_id, time_casa, time_visitante, escudo_casa, escudo_visitante, liga_nome, horario')
      .in('api_fixture_id', fixtureIds);
    if (jogosErr) throw jogosErr;

    const jogoPorFixture = new Map(
      ((jogos ?? []) as FutebolJogoDia[]).map((j) => [j.api_fixture_id, j]),
    );

    return historico
      .map((h) => {
        const jogo = jogoPorFixture.get(h.fixture_id);
        if (!jogo) return null;
        return {
          ...h,
          time_casa:        jogo.time_casa,
          time_visitante:   jogo.time_visitante,
          escudo_casa:      jogo.escudo_casa,
          escudo_visitante: jogo.escudo_visitante,
          liga_nome:        jogo.liga_nome,
          horario:          jogo.horario,
        } satisfies FutebolPartidaHistoricoItem;
      })
      .filter((item): item is FutebolPartidaHistoricoItem => item !== null);
  },

  async incrementarTermometro(params) {
    // Incremento validado no servidor (SECURITY DEFINER): vincula fixture_id
    // ao jogo ativo NAQUELA tela — bloqueia manipulação cross-tenant via WS.
    const { data, error } = await supabase.rpc('incrementar_termometro' as never, {
      p_tela_id:     params.tela_id,
      p_fixture_id:  params.fixture_id,
      p_time:        params.time,
      p_tipo:        params.tipo,
      p_session_id:  params.session_id,
    } as never);
    if (error) throw error;
    return data as string;
  },

  async buscarContagemTermometro(telaId, fixtureId) {
    const { data, error } = await supabase
      .from('futebol_termometro_contagem' as never)
      .select('*')
      .eq('tela_id', telaId)
      .eq('fixture_id', fixtureId);
    if (error) throw error;
    return (data ?? []) as unknown as FutebolTermometroContagem[];
  },

  async registrarVotoCraqueBagre(params) {
    const { data, error } = await supabase.rpc('registrar_voto_craque_bagre' as never, {
      p_tela_id:    params.tela_id,
      p_fixture_id: params.fixture_id,
      p_expiracao:  params.expiracao,
      p_session_id: params.session_id,
      p_voto:       params.voto,
    } as never);
    if (error) throw error;
    return data as string;
  },

  async buscarVotosCraqueBagre(telaId, fixtureId, expiracao) {
    const { data, error } = await supabase
      .from('futebol_craque_bagre_votos' as never)
      .select('*')
      .eq('tela_id', telaId)
      .eq('fixture_id', fixtureId)
      .eq('expiracao', expiracao);
    if (error) throw error;
    return (data ?? []) as unknown as FutebolCraqueBagreVoto[];
  },
};
